import { add, divide, multiply, vn } from 'vend-number'
import { useMemo } from 'react'
import { LineItem } from '../../../models/line-item'
import { useUserRule } from '../../../ng-compat/hooks'
import { RULES } from '../../../constants'

const MAX_DISCOUNT_PERCENT = 100

export const useLineItemDiscountRule = (lineItem: LineItem) => {
  const lineItemDiscountRule = useUserRule(RULES.LINE_ITEM_DISCOUNT) as null | {
    max_discount: number
  }

  const {
    minimumPriceAllowed,
    maxStackDiscountPercentAllowed,
  } = useMemo(() => {
    const displayBestDiscountPercent = vn(
      lineItem.displayBestDiscountPercent
    ).toNumber()
    const isPricebookMarkup = displayBestDiscountPercent < 0
    const bestPricebookDiscountPercent = Math.max(displayBestDiscountPercent, 0)
    const maxDiscountPercent = add(
      bestPricebookDiscountPercent,
      lineItemDiscountRule?.max_discount || 0
    )
    const maxStackDiscountPercentAllowed = Math.min(
      add(
        bestPricebookDiscountPercent,
        lineItemDiscountRule?.max_discount || 0
      ),
      MAX_DISCOUNT_PERCENT
    )

    const maxDiscountPossible = Math.min(
      maxDiscountPercent,
      MAX_DISCOUNT_PERCENT
    )
    const percentDecimal = divide(100 - maxDiscountPossible, 100)
    const discountPrice = isPricebookMarkup
      ? lineItem.displayBestDiscountPrice
      : lineItem.displayBasePrice
    const minimumPriceAllowed = multiply(discountPrice, percentDecimal)

    return {
      minimumPriceAllowed,
      maxStackDiscountPercentAllowed,
      discountPrice,
    }
  }, [
    lineItem.displayBasePrice,
    lineItem.displayBestDiscountPercent,
    lineItem.displayBestDiscountPrice,
    lineItemDiscountRule?.max_discount,
  ])

  if (!lineItemDiscountRule) {
    return {
      lineItemDiscountRule,
      maxStackDiscountPercentAllowed,
      minimumPriceAllowed,
      isPriceDiscountValid: () => false,
      isPercentDiscountValid: () => false,
    }
  }

  const isPriceDiscountValid = (price: string | number): boolean => {
    return vn(price).gte(minimumPriceAllowed)
  }

  const isPercentDiscountValid = (percent: string | number): boolean => {
    const percentDecimal = divide(100 - vn(percent).toNumber(), 100)
    const price = multiply(lineItem.displayBasePrice, percentDecimal)
    return vn(price).gte(minimumPriceAllowed)
  }

  return {
    lineItemDiscountRule,
    maxStackDiscountPercentAllowed,
    minimumPriceAllowed,
    isPriceDiscountValid,
    isPercentDiscountValid,
  }
}
